import { isHousePartner } from '@/services/advisoryPartner';
import { clientIdFromRoute, isSelfOwnedOnboarding, resolveOnboarding } from '@/services/onboarding_resolve';
import { normalizeSiteHostname } from '@/services/sites';
import { FLAG_HAMILTON_TRUST_CHAT, isFeatureEnabled } from '@/store/feature_flags';

export const MESSAGE_MAX_LENGTH = 2000;
export const EDIT_REQUEST_MAX_LENGTH = 2000;
export const PROPOSE_EDIT_REQUEST = 'propose_edit_request';
export const PROPOSAL_RESULTS = { submitted: 'submitted', cancelled: 'cancelled', dismissed: 'dismissed' };
export const PROPOSAL_PENDING = 'pending';
export const PROPOSAL_FAILURES = {
    busy: 'busy',
    failed: 'failed',
    intakeUnavailable: 'intake_unavailable',
    invalid: 'invalid',
    notPending: 'not_pending',
};
const CONFLICT_KINDS = {
    hamilton_busy: PROPOSAL_FAILURES.busy,
    intake_unavailable: PROPOSAL_FAILURES.intakeUnavailable,
    proposal_not_pending: PROPOSAL_FAILURES.notPending,
};

export const EDIT_REQUEST_STATUSES = { completed: 'completed', inProgress: 'in_progress' };
export const EDIT_REQUEST_FAILURES = { closed: 'closed', failed: 'failed', invalid: 'invalid' };
const EDIT_REQUEST_CLOSED_MESSAGE = 'This edit request is no longer in progress.';
const EDIT_REQUEST_FAILED_MESSAGE = 'Your edit request could not be updated. Please try again.';
const EDITED_AFTER_MS = 1000;

const CONFLICT_MESSAGE = 'Hamilton is still answering. Please wait a moment and try again.';
const PROPOSAL_BUSY_MESSAGE = 'Hamilton is still answering in this conversation. Please try again in a moment.';
const PROPOSAL_FAILED_MESSAGE = 'Your edit request could not be sent. Please try again.';
const FAILED_MESSAGE = 'Hamilton could not answer right now. Please try again.';
const LIMIT_MESSAGE =
    'You have reached the daily Hamilton message limit. Please try again later or talk with our team.';
const LIMIT_FALLBACK_MS = 60 * 1000;
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];
const TIMEOUT_MESSAGE = 'Hamilton took too long to answer. Please try again.';

const BOUND_ROUTE_NAMES = new Set(['document-review', 'onboarding-details', 'onboarding-form', 'project-detail']);

export function isPartnerSiteHost(user, currentHostname) {
    const siteHost = normalizeSiteHostname(user?.site_domain);
    return Boolean(siteHost) && siteHost !== normalizeSiteHostname(currentHostname);
}

export function isHamiltonAvailable({ flagEnabled, isLoggedIn, brandProfile, user, currentHostname }) {
    return Boolean(
        flagEnabled && isLoggedIn && isHousePartner(brandProfile) && !isPartnerSiteHost(user, currentHostname),
    );
}

export function isBindableRoute(route) {
    return Boolean(route) && BOUND_ROUTE_NAMES.has(route.name);
}

export function needsOnboardingList(route, onboardings) {
    return (
        isBindableRoute(route) &&
        !clientIdFromRoute(route) &&
        !resolveOnboarding(onboardings, route.params?.onboarding_id)
    );
}

export function resolveRouteOnboarding(route, onboardings, viewerUuid, { listLoaded = false } = {}) {
    if (!isBindableRoute(route)) return null;
    const list = onboardings || [];
    const fallbackSafe =
        listLoaded &&
        !clientIdFromRoute(route) &&
        Boolean(viewerUuid) &&
        list.length > 0 &&
        list.every(onboarding => onboarding.user?.uuid === viewerUuid);
    return resolveOnboarding(list, route.params?.onboarding_id, { allowFirstFallback: fallbackSafe });
}

export function boundIntakeUuid(route, onboardings, viewerUuid, options = {}) {
    if (!isFeatureEnabled(FLAG_HAMILTON_TRUST_CHAT)) return null;
    const onboarding = resolveRouteOnboarding(route, onboardings, viewerUuid, options);
    if (!onboarding?.uuid || !isSelfOwnedOnboarding(onboarding, viewerUuid, route)) return null;
    return onboarding.uuid;
}

export function isConversationRow(row) {
    return (row.role === 'user' || row.role === 'assistant') && !row.tool_name && Boolean(row.content);
}

function isProposalRow(row) {
    return row.role === 'assistant' && row.tool_name === PROPOSE_EDIT_REQUEST && Boolean(row.tool_call_id);
}

function parseToolResult(row) {
    try {
        const parsed = JSON.parse(row.content);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        return {};
    }
}

function proposalCard(row, outcome) {
    const proposed = typeof row.tool_arguments?.text === 'string' ? row.tool_arguments.text : '';
    const card = { toolCallId: row.tool_call_id, text: proposed };
    if (outcome?.result === PROPOSAL_RESULTS.submitted) {
        return { ...card, state: outcome.result, text: typeof outcome.text === 'string' ? outcome.text : proposed };
    }
    if (outcome?.result === PROPOSAL_RESULTS.cancelled) return { ...card, state: outcome.result };
    if (!outcome && row.is_pending) return { ...card, state: PROPOSAL_PENDING };
    return { ...card, state: PROPOSAL_RESULTS.dismissed };
}

const PYTHON_WHITESPACE =
    '\\t\\n\\v\\f\\r\\x1c-\\x20\\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const PYTHON_STRIP = new RegExp(`^[${PYTHON_WHITESPACE}]+|[${PYTHON_WHITESPACE}]+$`, 'g');

export function pythonStrip(text) {
    return text.replace(PYTHON_STRIP, '');
}

export function transcriptItems(rows) {
    const outcomes = new Map();
    for (const row of rows) {
        if (row.role === 'tool' && row.tool_name === PROPOSE_EDIT_REQUEST && row.tool_call_id) {
            outcomes.set(row.tool_call_id, parseToolResult(row));
        }
    }
    const items = [];
    for (const row of rows) {
        if (isConversationRow(row)) items.push({ kind: 'message', row });
        else if (isProposalRow(row)) {
            items.push({ kind: 'proposal', row, card: proposalCard(row, outcomes.get(row.tool_call_id)) });
        }
    }
    return items;
}

export function proposalFailure(error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    if (status === 409) {
        return { kind: CONFLICT_KINDS[data?.code] || PROPOSAL_FAILURES.busy, message: detail || PROPOSAL_BUSY_MESSAGE };
    }
    if (status === 400 && Array.isArray(data?.text) && data.text.length) {
        return { kind: PROPOSAL_FAILURES.invalid, message: String(data.text[0]) };
    }
    return { kind: PROPOSAL_FAILURES.failed, message: detail || PROPOSAL_FAILED_MESSAGE };
}

export function sourceSlugs(row) {
    if (row.role !== 'assistant') return [];
    const slugs = row.sources?.slugs;
    return Array.isArray(slugs) ? [...new Set(slugs.filter(Boolean))] : [];
}

export function turnFailure(error, now = Date.now()) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    if (status === 429) {
        const retryAt = Date.parse(data?.retry_after);
        return {
            kind: 'limited',
            message: detail || LIMIT_MESSAGE,
            retryAt: Number.isNaN(retryAt) ? now + LIMIT_FALLBACK_MS : retryAt,
        };
    }
    if (status === 409) return { kind: 'conflict', message: detail || CONFLICT_MESSAGE };
    const fieldErrors = status === 400 ? data?.content : null;
    if (Array.isArray(fieldErrors) && fieldErrors.length) {
        return { kind: 'invalid', message: String(fieldErrors[0]) };
    }
    if (status === 504 || (!status && TIMEOUT_CODES.includes(error?.code))) {
        return { kind: 'timeout', message: TIMEOUT_MESSAGE };
    }
    return { kind: 'failed', message: detail || FAILED_MESSAGE };
}

export function editRequestFailure(error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    if (status === 409) return { kind: EDIT_REQUEST_FAILURES.closed, message: detail || EDIT_REQUEST_CLOSED_MESSAGE };
    if (status === 404) return { kind: EDIT_REQUEST_FAILURES.closed, message: EDIT_REQUEST_CLOSED_MESSAGE };
    if (status === 400 && Array.isArray(data?.text) && data.text.length) {
        return { kind: EDIT_REQUEST_FAILURES.invalid, message: String(data.text[0]) };
    }
    return { kind: EDIT_REQUEST_FAILURES.failed, message: detail || EDIT_REQUEST_FAILED_MESSAGE };
}

export function isEditedRequest(request) {
    if (request?.status !== EDIT_REQUEST_STATUSES.inProgress) return false;
    return Date.parse(request.modified) - Date.parse(request.created) >= EDITED_AFTER_MS;
}
